'use strict';

const { parseTopBids, parseBidData } = require('./adformResponseParserHelpers');
const { generateBannerHtml } = require('./adformContentBuilderHelpers');
const adformConstants = require('../adformConstants');
const adformEventDispatcher = require('../adformEventDispatcher');

/**
 * Returns array of parsed bid objects that can be used to generate html (img ad with link).
 * @param {Array} topBids - array of bids from response (sorted by price)
 * @return {Object[]} - array of parsed bid objects
 */
function getValidBids(topBids) {
    let validBids = [];
    for (let i = 0; i < topBids.length; i++) {
        const bid = topBids[i];
        const parsedBid = parseBidData(bid);
        const bannerHtml = generateBannerHtml(parsedBid, true);
        if (!parsedBid || !bannerHtml) {
            // eslint-disable-next-line no-continue
            continue;
        }

        validBids.push(parsedBid);
    }

    return validBids;
}

/**
 * Handles when adform doesn't return bids (informs other modules e.g. sliders to remove loader)
 * @param {Object} containerTypeConfig - config in adformConstants.js holding tagid, img sizes and similar (e.g. CONTAINER_TYPE_CATEGORY_CONFIG)
 * @param {Object} additionalEventData - additional data needed to process adform data
 */
function handleNoBids(containerTypeConfig, additionalEventData) {
    const eventData = {
        detail: {
            containerTypeConfig: containerTypeConfig,
            additionalEventData: additionalEventData
        }
    };
    adformEventDispatcher.dispatch(adformConstants.NO_BIDS_CUSTOM_EVENT_NAME, eventData);
}

/**
 * Handles adform returned bids (informs other modules e.g. sliders to prepend ads and to remove loader)
 * @param {Object} containerTypeConfig - config in adformConstants.js holding tagid, img sizes and similar (e.g. CONTAINER_TYPE_CATEGORY_CONFIG)
 * @param {Object[]} bids - array of parsed bid objects (img, links...)
 * @param {Object} additionalEventData - additional data needed to process adform data
 */
function handleHasBids(containerTypeConfig, bids, additionalEventData) {
    const eventData = {
        detail: {
            containerTypeConfig: containerTypeConfig,
            bids: bids,
            additionalEventData: additionalEventData
        }
    };
    adformEventDispatcher.dispatch(adformConstants.BIDS_READY_CUSTOM_EVENT_NAME, eventData);
}

/**
 * Handles when adform request if not valid
 * @param {Object} containerTypeConfig - config in adformConstants.js holding tagid, img sizes and similar (e.g. CONTAINER_TYPE_CATEGORY_CONFIG)
 * @param {Object} additionalEventData - additional data needed to process adform data
 */
function handleInvalidRequest(containerTypeConfig, additionalEventData) {
    handleNoBids(containerTypeConfig, additionalEventData);
}

/**
 * Handles when adform request if not valid
 * @param {Object} containerTypeConfig - config in adformConstants.js holding tagid, img sizes and similar (e.g. CONTAINER_TYPE_CATEGORY_CONFIG)
 * @param {Object} error - error from try and catch
 * @param {Object} additionalEventData - additional data needed to process adform data
 */
function handleError(containerTypeConfig, error, additionalEventData) {
    // todo: handle error
    // eslint-disable-next-line no-unused-vars
    let e = error;
    handleNoBids(containerTypeConfig, additionalEventData);
}

/**
 * Handles adform 200 code response that has bids data.
 * Converts bids from response into parsed objects that can be used for inserting ads (img, link...) and sends them with dispatchEvent.
 * If not valid bids, informs other modules (e.g. slider to remove placeholder loader...)
 *
 * @param {Object} containerTypeConfig - config in adformConstants.js holding tagid, img sizes and similar (e.g. CONTAINER_TYPE_CATEGORY_CONFIG)
 * @param {Object} data - adform response
 * @param {Object} additionalEventData - additional data needed to process adform data
 */
function handleBidsResponse(containerTypeConfig, data, additionalEventData) {
    try {
        const topBids = parseTopBids(data, containerTypeConfig.numOfBids);
        if (!topBids.length) {
            handleNoBids(containerTypeConfig, additionalEventData);
            return;
        }

        let validBids = getValidBids(topBids);
        if (!validBids.length) {
            handleNoBids(containerTypeConfig, additionalEventData);
            return;
        }
        handleHasBids(containerTypeConfig, validBids, additionalEventData);
    } catch (e) {
        handleError(containerTypeConfig, e, additionalEventData);
    }
}

module.exports = {
    handleInvalidRequest: handleInvalidRequest,
    handleNoBids: handleNoBids,
    handleError: handleError,
    handleBidsResponse: handleBidsResponse
};
